import { isSuperAdmin } from "modules/auth/authorization/roleChecks";
import { Button, Input, RoleType, useTitle } from "modules/common";
import { PanelMenu, PanelMenuItem } from "modules/common/components/PanelMenu";
import useDebounced from "modules/common/hooks/useDebounced";
import {
    useGetEducationDocumentCategories,
    useGetEducationDocuments,
} from "modules/educationDocuments";
import {
    EducationDocumentCategoryModal,
    EducationDocumentDetailsModal,
    EducationDocumentsTable,
} from "modules/educationDocuments/components";
import { EducationDocumentsFilters } from "modules/educationDocuments/components/EducationDocumentsFilters";
import { DocumentType } from "modules/educationDocuments/constants";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
    EducationDocumentCategory,
    GetEducationDocumentRequest,
} from "services/educationDocuments";
import { EducationDocument } from "services/educationDocuments/response";
import { RootState } from "store";
import { AccessibilityContext } from "./AccessibilityContext";

const INPUT_DEBOUNCE_TIME = 500;

const initialRequest: GetEducationDocumentRequest = {
    page: 1,
    pageSize: 10,
    search: null,
};

export const EducationDocuments: React.FC = () => {
    useTitle("Dokumentacija - eSavjetnik");

    const [menuItem, setMenuItem] = useState<PanelMenuItem>();
    const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
    const [isOnlyOneChecked, setIsOnlyOneChecked] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [professionalAreaFilter, setProfessionalAreaFilter] = useState<string[]>([]);
    const [documentTypeFilter, setDocumentTypeFilter] = useState<DocumentType[]>([]);
    const [selectedRows, setSelectedRows] = useState<EducationDocument[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<EducationDocumentCategory>();
    const [latestSelectedCategory, setLatestSelectedCategory] =
        useState<EducationDocumentCategory>();
    const [searchTerm, setSearchTerm] = useState<string | null>(null);
    const search = useDebounced(searchTerm, INPUT_DEBOUNCE_TIME);
    const { profile } = useSelector((state: RootState) => state.profile);

    const {
        data: educationDocumentCategoriesData,
        isLoading: isEducationDocumentCategoriesLoading,
    } = useGetEducationDocumentCategories();

    const menuItems = useMemo(
        () =>
            educationDocumentCategoriesData?.map(category => ({
                link: category.guid,
                label: category.name,
            })),
        [educationDocumentCategoriesData]
    );

    const [request, setRequest] = useState<GetEducationDocumentRequest>({
        ...initialRequest,
        categoryGuid: menuItems?.[0]?.link,
    });

    const { data: educationDocumentsData, isLoading: isEducationDocumentsLoading } =
        useGetEducationDocuments(request);

    useEffect(() => {
        setMenuItem(menuItems?.[0]);
        setSelectedCategory(educationDocumentCategoriesData?.[0]);
        setRequest({ ...request, categoryGuid: menuItems?.[0]?.link });
    }, [menuItems]);

    useEffect(() => {
        setRequest({
            ...request,
            search: search,
        });
    }, [search]);

    useEffect(() => {
        setRequest({
            ...request,
            professionalAreaGuid:
                professionalAreaFilter.length > 0 ? professionalAreaFilter : undefined,
            documentType: documentTypeFilter ?? undefined,
        });
    }, [professionalAreaFilter, documentTypeFilter]);

    const handleSearchDocument = (search: string) => {
        setSearchTerm(search);
    };

    const handlePageChange = (value: number) => {
        setRequest(prev => ({ ...prev, page: value }));
    };

    const handlePageSizeChange = (value: number) => {
        setRequest(prev => ({ ...prev, page: 1, pageSize: value }));
    };

    const handleModalClose = () => {
        setIsCategoryModalOpen(false);
        setSelectedCategory(latestSelectedCategory);
    };

    const { lowVision } = useContext(AccessibilityContext);

    return (
        <div className="education-documents-container">
            <div className="panel-menu-container">
                <div className="education-documents-search">
                    <Input
                        id="search"
                        type="text"
                        size="large"
                        isValid={true}
                        name="search"
                        errorMessage=""
                        placeholder="Pretraži dokumente..."
                        onChange={e => handleSearchDocument(e.target.value)}
                    />
                </div>
                <div className="education-document-categories-menu">
                    <PanelMenu
                        items={menuItems}
                        selectedItem={menuItem}
                        isRightIcon
                        onItemClicked={item => {
                            setMenuItem(item);
                            setRequest({ ...request, categoryGuid: item.link, page: 1 });
                            setSelectedCategory(
                                educationDocumentCategoriesData?.find(
                                    category => category.guid === item.link
                                )
                            );
                            setSelectedRows([]);
                        }}
                    />
                    {isSuperAdmin(profile?.roleType?.value ?? 0) && (
                        <div className="education-document-categories-menu-buttons">
                            <Button
                                size="small"
                                variant="primary-ghost"
                                lowVision={lowVision}
                                disabled={
                                    isEducationDocumentCategoriesLoading ||
                                    !selectedCategory ||
                                    profile?.roleType?.value !== RoleType.SuperAdmin
                                }
                                onClick={() => {
                                    setLatestSelectedCategory(selectedCategory);
                                    setIsCategoryModalOpen(Boolean(selectedCategory));
                                }}
                            >
                                UREDI
                            </Button>
                            <Button
                                size="small"
                                variant="primary"
                                lowVision={lowVision}
                                disabled={
                                    isEducationDocumentCategoriesLoading ||
                                    profile?.roleType?.value !== RoleType.SuperAdmin
                                }
                                onClick={() => {
                                    setLatestSelectedCategory(selectedCategory);
                                    setSelectedCategory(undefined);
                                    setIsCategoryModalOpen(true);
                                }}
                            >
                                NOVA KATEGORIJA
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            <div className="education-documents-content">
                <div className="education-documents-heading">
                    <div className="education-documents-filters">
                        <div className="documents-filter-header-title">DOKUMENTACIJA</div>
                        <div className="header-filter-separator" />
                        <EducationDocumentsFilters
                            professionalAreaFilter={professionalAreaFilter}
                            documentTypeFilter={documentTypeFilter}
                            setProfessionalAreaFilter={setProfessionalAreaFilter}
                            setDocumentTypeFilter={setDocumentTypeFilter}
                            setCurrentPage={() => handlePageChange(1)}
                        />
                    </div>
                    {isSuperAdmin(profile?.roleType?.value ?? 0) && (
                        <Button
                            size="small"
                            className="education-documents-button"
                            variant="primary"
                            lowVision={lowVision}
                            disabled={
                                isEducationDocumentCategoriesLoading ||
                                isEducationDocumentsLoading ||
                                profile?.roleType?.value !== RoleType.SuperAdmin
                            }
                            onClick={() => {
                                setSelectedRows([]);
                                setIsOnlyOneChecked(false);
                                setIsDocumentModalOpen(true);
                            }}
                        >
                            DODAJ
                        </Button>
                    )}
                </div>

                {menuItem && (
                    <EducationDocumentsTable
                        educationDocuments={educationDocumentsData}
                        isLoading={isEducationDocumentsLoading}
                        page={request.page}
                        pageSize={request.pageSize}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        setIsDocumentModalOpen={setIsDocumentModalOpen}
                        setIsOnlyOneChecked={setIsOnlyOneChecked}
                    />
                )}
            </div>

            {isDocumentModalOpen && (
                <EducationDocumentDetailsModal
                    educationDocumentsData={educationDocumentsData}
                    isOpen={isDocumentModalOpen}
                    handleClose={() => {
                        setIsDocumentModalOpen(false);
                        setIsOnlyOneChecked(false);
                    }}
                    educationDocuments={selectedRows}
                    isChecked={isOnlyOneChecked}
                />
            )}

            <EducationDocumentCategoryModal
                isOpen={isCategoryModalOpen}
                handleClose={handleModalClose}
                educationDocumentCategory={selectedCategory}
                hasDocuments={Boolean(educationDocumentsData?.items.length)}
            />
        </div>
    );
};
